<script setup lang="ts">
useSeoMeta({
  title: 'Privacy Policy',
  description: 'Informativa sul trattamento dei dati personali'
})

const lastUpdate = '14/03/2026'
</script>

<template>
  <div class="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
    <!-- Top bar (stile landing) -->
    <LandingNav />

    <!-- Hero semplice coerente -->
    <section class="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
      <UContainer class="py-14 md:py-20">
        <div class="max-w-3xl">
          <UBadge
            class="mb-4 border border-slate-200 bg-white/80 text-slate-700 dark:border-slate-800 dark:bg-slate-950/70 dark:text-slate-200"
          >
            Documentazione legale
          </UBadge>

          <h1 class="text-4xl font-bold tracking-tight text-slate-900 dark:text-white md:text-5xl">
            Privacy Policy
          </h1>

          <p class="mt-4 text-lg text-slate-700 dark:text-slate-300">
            Qui trovi come trattiamo i dati personali quando visiti il sito o richiedi informazioni sui servizi.
          </p>
        </div>
      </UContainer>
    </section>

    <!-- Contenuto in card (stile landing CTA) -->
    <section>
      <UContainer class="py-10 md:py-14">
        <div class="rounded-3xl border border-slate-200 bg-white p-8 dark:border-slate-800 dark:bg-slate-950 md:p-12">
          <div class="prose prose-slate dark:prose-invert max-w-none">
            <p><strong>Ultimo aggiornamento:</strong> {{ lastUpdate }}</p>
            <div class="hero">
              <div class="hero-label">
                Documentazione legale
              </div>
              <h1>Privacy Policy</h1>
              <div class="hero-meta">
                Ultimo aggiornamento: 14 marzo 2026
              </div>
            </div>

            <hr class="divider">

            <main class="content">
              <!-- 1 -->
              <div class="section">
                <div class="section-number">
                  01
                </div>
                <h2>Titolare del trattamento</h2>
                <p>
                  Il Titolare del trattamento è <strong>Luca Margonar</strong> (persona fisica), con sede in Via del
                  Tiglio 19, 6900 Lugano, Svizzera.
                </p>
                <div class="highlight-box">
                  <p>Contatto privacy: <strong>purejoulech@gmail.com</strong></p>
                </div>
              </div>

              <!-- 2 -->
              <div class="section">
                <div class="section-number">
                  02
                </div>
                <h2>Quadro normativo applicabile</h2>
                <p>
                  Il trattamento dei dati personali è disciplinato dalla <strong>Legge federale svizzera sulla
                    protezione dei dati (nLPD)</strong>, in vigore dal 1° settembre 2023. Poiché i servizi sono offerti
                  anche a persone residenti in Italia e nell'Unione Europea, si applica altresì il <strong>Regolamento
                    (UE) 2016/679 (GDPR)</strong>.
                </p>
              </div>

              <!-- 3 -->
              <div class="section">
                <div class="section-number">
                  03
                </div>
                <h2>Dati raccolti</h2>
                <p><strong>Dati forniti volontariamente</strong></p>
                <ul>
                  <li>Dati identificativi e di contatto (nome, cognome, email, anno di nascita, città)</li>
                  <li>Informazioni sportive e di coaching inserite nei moduli o comunicate direttamente</li>
                  <li>Comunicazioni via email o altri canali diretti</li>
                </ul>
                <p><strong>Dati raccolti automaticamente</strong></p>
                <ul>
                  <li>
                    Dati di navigazione tecnici (indirizzo IP, browser, OS, pagine visitate, orari di accesso)
                    raccolti dall'infrastruttura di hosting
                  </li>
                </ul>
              </div>

              <!-- 4 -->
              <div class="section">
                <div class="section-number">
                  04
                </div>
                <h2>Finalità e basi giuridiche</h2>
                <div class="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Finalità</th>
                        <th>Base giuridica</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Rispondere a richieste di contatto</td>
                        <td>Legittimo interesse / misure precontrattuali (art. 6.1.b GDPR)</td>
                      </tr>
                      <tr>
                        <td>Erogazione del servizio di coaching</td>
                        <td>Esecuzione del contratto (art. 6.1.b GDPR)</td>
                      </tr>
                      <tr>
                        <td>Adempimenti legali e fiscali</td>
                        <td>Obbligo di legge (art. 6.1.c GDPR)</td>
                      </tr>
                      <tr>
                        <td>Sicurezza del sito e prevenzione abusi</td>
                        <td>Legittimo interesse (art. 6.1.f GDPR)</td>
                      </tr>
                      <tr>
                        <td>Invio newsletter / comunicazioni commerciali</td>
                        <td>Consenso esplicito (art. 6.1.a GDPR) — solo se attivato</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- 5 -->
              <div class="section">
                <div class="section-number">
                  05
                </div>
                <h2>Conservazione dei dati</h2>
                <ul>
                  <li>
                    <strong>Dati di contatto e coaching:</strong> per tutta la durata del rapporto e per un massimo di
                    3 anni dalla sua conclusione, salvo obblighi di legge più lunghi
                  </li>
                  <li><strong>Dati di navigazione tecnici:</strong> gestiti secondo le politiche di Vercel</li>
                  <li>
                    <strong>Dati fiscali e contabili:</strong> per il periodo previsto dalla normativa svizzera
                    (generalmente 10 anni)
                  </li>
                </ul>
              </div>

              <!-- 6 -->
              <div class="section">
                <div class="section-number">
                  06
                </div>
                <h2>Destinatari e responsabili del trattamento</h2>
                <p>
                  I dati possono essere condivisi con i seguenti fornitori terzi, nominati ove necessario come
                  Responsabili del trattamento:
                </p>
                <div class="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Fornitore</th>
                        <th>Ruolo</th>
                        <th>Dati trattati</th>
                        <th>Informativa</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Vercel Inc.</strong> (USA)</td>
                        <td>Hosting e infrastruttura</td>
                        <td>Dati di navigazione, dati dei form</td>
                        <td>
                          <a href="https://vercel.com/legal/privacy-policy" target="_blank">vercel.com/legal/privacy-policy</a>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>TrainingPeaks</strong> (USA)</td>
                        <td>Piattaforma di coaching</td>
                        <td>Dati sportivi e di allenamento</td>
                        <td>
                          <a href="https://www.trainingpeaks.com/about/privacy-policy/" target="_blank">trainingpeaks.com/privacy-policy</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p>
                  Il trasferimento verso fornitori extra-SEE avviene nel rispetto delle garanzie previste dal GDPR
                  (Clausole Contrattuali Standard) e dalla nLPD. I dati non sono venduti né ceduti a terzi per finalità
                  di marketing.
                </p>
              </div>

              <!-- 7 -->
              <div class="section">
                <div class="section-number">
                  07
                </div>
                <h2>Cookie e tracciamento</h2>
                <p>
                  Il sito utilizza esclusivamente <strong>cookie tecnici</strong> necessari al funzionamento. Non sono
                  attivi cookie di profilazione o di tracciamento pubblicitario. Se in futuro venissero introdotti
                  cookie analitici o di terze parti, la presente policy sarà aggiornata e sarà richiesto il consenso ove
                  previsto dalla normativa.
                </p>
              </div>

              <!-- 8 -->
              <div class="section">
                <div class="section-number">
                  08
                </div>
                <h2>Diritti dell'interessato</h2>
                <div class="rights-grid">
                  <div class="right-card">
                    <div class="right-title">
                      Accesso
                    </div>
                    <p>Ottenere conferma del trattamento e riceverne copia</p>
                  </div>
                  <div class="right-card">
                    <div class="right-title">
                      Rettifica
                    </div>
                    <p>Correggere dati inesatti o incompleti</p>
                  </div>
                  <div class="right-card">
                    <div class="right-title">
                      Cancellazione
                    </div>
                    <p>Richiedere il diritto all'oblio nei casi previsti</p>
                  </div>
                  <div class="right-card">
                    <div class="right-title">
                      Limitazione
                    </div>
                    <p>Limitare il trattamento in determinati casi</p>
                  </div>
                  <div class="right-card">
                    <div class="right-title">
                      Portabilità
                    </div>
                    <p>Ricevere i dati in formato strutturato e leggibile</p>
                  </div>
                  <div class="right-card">
                    <div class="right-title">
                      Opposizione
                    </div>
                    <p>Opporti al trattamento basato su legittimo interesse</p>
                  </div>
                  <div class="right-card">
                    <div class="right-title">
                      Revoca del consenso
                    </div>
                    <p>Revocare in qualsiasi momento il consenso prestato</p>
                  </div>
                </div>
                <p style="margin-top:1rem;">
                  Per esercitare questi diritti: <strong>purejoulech@gmail.com</strong>
                </p>
              </div>

              <!-- 9 -->
              <div class="section">
                <div class="section-number">
                  09
                </div>
                <h2>Reclami all'autorità di controllo</h2>
                <div class="authority-cards">
                  <div class="authority-card">
                    <div class="country">
                      Svizzera
                    </div>
                    <div class="name">
                      IFPDT
                    </div>
                    <a href="https://www.ifpdt.admin.ch" target="_blank">ifpdt.admin.ch →</a>
                  </div>
                  <div class="authority-card">
                    <div class="country">
                      Italia / UE
                    </div>
                    <div class="name">
                      Garante Privacy
                    </div>
                    <a href="https://www.garanteprivacy.it" target="_blank">garanteprivacy.it →</a>
                  </div>
                </div>
              </div>

              <!-- 10 -->
              <div class="section">
                <div class="section-number">
                  10
                </div>
                <h2>Modifiche alla presente policy</h2>
                <p>
                  Eventuali aggiornamenti saranno pubblicati su questa pagina con indicazione della data di revisione.
                  In caso di modifiche sostanziali, gli utenti registrati saranno informati via email.
                </p>
              </div>
            </main>
          </div>
        </div>
      </UContainer>
    </section>

    <LandingFooter />
  </div>
</template>

<style>
html {
  scroll-behavior: smooth;
}

/* HERO */
.hero {
  padding: 5rem 2rem 3rem;
  max-width: 760px;
  margin: 0 auto;
}

.hero-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 1.2rem;
}

.hero h1 {
  font-family: 'Syne', sans-serif;
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 1rem;
}

.hero-meta {
  font-size: 0.85rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hero-meta::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
}

/* DIVIDER */
.divider {
  max-width: 760px;
  margin: 0 auto 3rem;
  padding: 0 2rem;
  border: none;
  border-top: 1px solid var(--border);
}

/* CONTENT */
.content {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 2rem 6rem;
}

.section {
  margin-bottom: 3.5rem;
  opacity: 0;
  transform: translateY(16px);
  animation: fadeUp 0.5s forwards;
}

.section:nth-child(1) {
  animation-delay: 0.05s;
}

.section:nth-child(2) {
  animation-delay: 0.10s;
}

.section:nth-child(3) {
  animation-delay: 0.15s;
}

.section:nth-child(4) {
  animation-delay: 0.20s;
}

.section:nth-child(5) {
  animation-delay: 0.25s;
}

.section:nth-child(6) {
  animation-delay: 0.30s;
}

.section:nth-child(7) {
  animation-delay: 0.35s;
}

.section:nth-child(8) {
  animation-delay: 0.40s;
}

.section:nth-child(9) {
  animation-delay: 0.45s;
}

.section:nth-child(10) {
  animation-delay: 0.50s;
}

@keyframes fadeUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.section-number {
  font-family: 'Syne', sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 0.5rem;
}

.section h2 {
  font-family: 'Syne', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 1rem;
  color: var(--text);
}

.section p {
  color: var(--text-secondary);
  margin-bottom: 0.8rem;
  font-weight: 300;
}

.section p strong {
  color: var(--text);
  font-weight: 500;
}

.section ul {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0.8rem;
}

.section ul li {
  color: var(--text-secondary);
  font-weight: 300;
  padding: 0.35rem 0 0.35rem 1.2rem;
  position: relative;
  border-bottom: 1px solid var(--border);
}

.section ul li:last-child {
  border-bottom: none;
}

.section ul li::before {
  content: '—';
  position: absolute;
  left: 0;
  color: var(--accent);
  font-size: 0.75rem;
}

/* TABLE */
.table-wrap {
  overflow-x: auto;
  margin: 1rem 0;
  border-radius: 8px;
  border: 1px solid var(--border);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

thead tr {
  background: var(--accent-dim);
}

th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-family: 'Syne', sans-serif;
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--accent);
  white-space: nowrap;
}

td {
  padding: 0.75rem 1rem;
  color: var(--text-secondary);
  font-weight: 300;
  vertical-align: top;
  border-top: 1px solid var(--border);
}

td strong {
  color: var(--text);
  font-weight: 500;
}

td a {
  color: var(--accent);
  text-decoration: none;
  font-size: 0.8rem;
}

td a:hover {
  text-decoration: underline;
}

tbody tr:hover td {
  background: rgba(255, 255, 255, 0.015);
}

/* HIGHLIGHT BOX */
.highlight-box {
  background: var(--accent-dim);
  border-left: 3px solid var(--accent);
  border-radius: 0 6px 6px 0;
  padding: 1rem 1.2rem;
  margin: 1rem 0;
}

.highlight-box p {
  color: var(--text-secondary);
  font-weight: 300;
  margin: 0;
}

.highlight-box strong {
  color: var(--text);
  font-weight: 500;
}

/* RIGHTS GRID */
.rights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.right-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 0.9rem 1rem;
  transition: border-color 0.2s;
}

.right-card:hover {
  border-color: var(--accent);
}

.right-card .right-title {
  font-family: 'Syne', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 0.3rem;
}

.right-card p {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
}

/* AUTHORITY CARDS */
.authority-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1rem;
}

@media (max-width: 500px) {
  .authority-cards {
    grid-template-columns: 1fr;
  }
}

.authority-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 1rem 1.1rem;
}

.authority-card .country {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 0.3rem;
}

.authority-card .name {
  font-family: 'Syne', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 0.4rem;
}

.authority-card a {
  font-size: 0.78rem;
  color: var(--text-muted);
  text-decoration: none;
}

.authority-card a:hover {
  color: var(--accent);
}

/* FOOTER */
footer {
  border-top: 1px solid var(--border);
  padding: 2rem;
  text-align: center;
  font-size: 0.8rem;
  color: var(--text-muted);
}

footer a {
  color: var(--text-muted);
  text-decoration: none;
  margin: 0 0.5rem;
}

footer a:hover {
  color: var(--accent);
}
</style>
